import React, { useEffect, useState, useRef } from 'react';
import { MenuIcon, XIcon, ChevronDownIcon } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
export const Header: React.FC = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDienstenOpen, setIsDienstenOpen] = useState(false);
  const [isMobileDienstenOpen, setIsMobileDienstenOpen] = useState(false);
  const dienstenRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dienstenRef.current && !dienstenRef.current.contains(event.target as Node)) {
        setIsDienstenOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setIsMobileMenuOpen(false);
  };
  const handleNavigation = (path: string, sectionId?: string) => {
    setIsMobileMenuOpen(false);
    setIsDienstenOpen(false);
    setIsMobileDienstenOpen(false);
    if (location.pathname === '/' && sectionId) {
      scrollToSection(sectionId);
    } else if (path === '/' && sectionId) {
      navigate('/');
      setTimeout(() => {
        scrollToSection(sectionId);
      }, 100);
    } else {
      navigate(path);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };
  const isHome = location.pathname === '/';
  const dienstenItems = [{
    label: 'Websites',
    action: () => handleNavigation('/websites')
  }, {
    label: 'Digitale vormgeving',
    action: () => handleNavigation('/vormgeving')
  }, {
    label: 'Webapplicaties',
    action: () => handleNavigation('/webapplicatie-laten-maken')
  }, {
    label: 'Hosting',
    action: () => handleNavigation('/hosting')
  }, {
    label: 'E-mail',
    action: () => handleNavigation('/email')
  }, {
    label: 'SEO',
    action: () => handleNavigation('/seo')
  }, {
    label: 'CorePrint',
    action: () => window.open('https://coreprint.nl/', '_blank')
  }];
  const navItems: {
    label: string;
    action?: () => void;
    isDropdown?: boolean;
    highlight?: boolean;
    active?: boolean;
  }[] = [{
    label: 'Home',
    action: () => handleNavigation('/'),
    active: isHome
  }, {
    label: 'Diensten',
    isDropdown: true
  }, {
    label: 'Cases',
    action: () => handleNavigation('/', 'client-cases')
  }, {
    label: 'Over ons',
    action: () => handleNavigation('/over-ons')
  }, {
    label: 'Bibliotheek',
    action: () => handleNavigation('/bibliotheek')
  }, {
    label: 'Vacatures',
    action: () => handleNavigation('/sollicitatie')
  }];
  return <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${isScrolled ? 'shadow-md' : ''}`} style={{
      height: isScrolled ? '64px' : '80px'
    }} data-id="element-471" data-mp="1544kgwimf8ld">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full" data-id="element-472" data-mp="1f3lpdbq1tiga">
          <div className="flex items-center justify-between h-full" data-id="element-473" data-mp="1a48z0gzyxt8y">
            {/* Logo */}
            <button onClick={() => handleNavigation('/')} className="flex-shrink-0 hover:opacity-80 transition-opacity" aria-label="Noord Digital, naar de homepage" data-id="element-474" data-mp="3gup164bod32f">
              <img src="https://cdn.magicpatterns.com/uploads/9aWYg1KoQ1vAxmEDFRCMUb/Logo_Noord.png" alt="Noord Digital" className={`w-auto transition-all duration-300 ${isScrolled ? 'h-8' : 'h-10'}`} data-id="element-475" data-mp="3bxgg9vz56kxh" />
            </button>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1" data-id="element-476" data-mp="2cwlbkf30p29j">
              {navItems.map((item, index) => {
              if (item.isDropdown) {
                return <div key={index} ref={dienstenRef} className="relative" data-id="element-477" data-mp="2gw3skzrpwt73">
                      <button onClick={() => setIsDienstenOpen(!isDienstenOpen)} className="flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 text-[#111827] hover:bg-[#F7F8FA]" data-id="element-478" data-mp="1ka0h35r8wa1r">
                        {item.label}
                        <ChevronDownIcon className={`h-4 w-4 ml-1 transition-transform duration-200 ${isDienstenOpen ? 'rotate-180' : ''}`} data-id="element-479" data-mp="1xzu7vtmb94e4" />
                      </button>

                      {isDienstenOpen && <div className="absolute top-full left-0 mt-1 w-52 bg-white rounded-xl shadow-lg border border-gray-100 py-2 z-50" data-id="element-480" data-mp="15irl8ewr1sg6">
                          {dienstenItems.map((subItem, subIndex) => <button key={subIndex} onClick={subItem.action} className="w-full text-left px-4 py-2.5 text-sm text-[#111827] hover:bg-[#F7F8FA] hover:text-[#004AC7] transition-colors" data-id="element-481" data-mp="t0nu7n20ib8l" data-mpi={subIndex}>
                              {subItem.label}
                            </button>)}
                        </div>}
                    </div>;
              }
              return <button key={index} onClick={item.action} aria-current={item.active ? 'page' : undefined} className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${item.active ? 'text-[#004AC7] bg-[#E6F0FF]' : item.highlight ? 'text-[#004AC7] font-bold hover:bg-[#E6F0FF]' : 'text-[#111827] hover:bg-[#F7F8FA]'}`} data-id="element-482" data-mp="f2h6v0uovtn6" data-mpi={index}>
                    {item.label}
                  </button>;
            })}
            </nav>

            {/* Desktop CTA + Mobile CTA & Hamburger */}
            <div className="flex items-center gap-3" data-id="element-483" data-mp="3ni0u9j8oihub">
              {/* CTA Button - visible on all screens */}
              <button onClick={() => handleNavigation('/', 'contact')} className="px-5 py-2.5 text-white font-semibold text-sm hover:opacity-90 transition-all duration-200 shadow-sm" style={{
              backgroundColor: '#004AC7',
              borderRadius: '6px'
            }} data-id="element-484" data-mp="3drix707m6y14">
                Kennismaking
              </button>

              {/* Mobile Menu Button */}
              <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="lg:hidden p-2 rounded-lg hover:bg-[#F7F8FA] transition-colors" aria-label="Toggle menu" data-id="element-485" data-mp="f8qzmqfr04v">
                {isMobileMenuOpen ? <XIcon className="h-6 w-6 text-[#111827]" data-id="element-486" data-mp="3msqubeye9stm" /> : <MenuIcon className="h-6 w-6 text-[#111827]" data-id="element-487" data-mp="3mrxsmsealtic" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {isMobileMenuOpen && <>
          <div className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} data-id="element-488" data-mp="o3ry3yxr16vd" />

          <div className="fixed left-0 right-0 z-40 bg-white shadow-2xl lg:hidden rounded-b-2xl" style={{
        top: isScrolled ? '64px' : '80px'
      }} data-id="element-489" data-mp="28tnjeidn4shf">
            <nav className="px-4 py-6 space-y-1" data-id="element-490" data-mp="23szvw6ometry">
              {navItems.map((item, index) => {
            if (item.isDropdown) {
              return <div key={index} data-id="element-491" data-mp="2dkdt27t3nhhx">
                      <button onClick={() => setIsMobileDienstenOpen(!isMobileDienstenOpen)} className="w-full flex items-center justify-between px-4 py-3 text-base font-medium rounded-lg text-[#111827] hover:bg-[#F7F8FA] transition-all duration-200" data-id="element-492" data-mp="2dlfe953txkdd">
                        {item.label}
                        <ChevronDownIcon className={`h-5 w-5 transition-transform duration-200 ${isMobileDienstenOpen ? 'rotate-180' : ''}`} data-id="element-493" data-mp="1ywsriibvhqc4" />
                      </button>
                      {isMobileDienstenOpen && <div className="ml-4 mt-1 space-y-1 border-l-2 border-[#E6F0FF] pl-3" data-id="element-494" data-mp="3a9v30yz47ppf">
                          {dienstenItems.map((subItem, subIndex) => <button key={subIndex} onClick={subItem.action} className="w-full text-left px-4 py-2.5 text-sm text-[#6B7280] hover:text-[#004AC7] hover:bg-[#F7F8FA] rounded-lg transition-colors" data-id="element-495" data-mp="36bxd1txiszkz" data-mpi={subIndex}>
                              {subItem.label}
                            </button>)}
                        </div>}
                    </div>;
            }
            return <button key={index} onClick={item.action} aria-current={item.active ? 'page' : undefined} className={`w-full text-left px-4 py-3 text-base font-medium rounded-lg transition-all duration-200 ${item.active ? 'text-[#004AC7] bg-[#E6F0FF]' : item.highlight ? 'text-[#004AC7] font-bold bg-[#E6F0FF]' : 'text-[#111827] hover:bg-[#F7F8FA]'}`} data-id="element-496" data-mp="1iclkcojiugcx" data-mpi={index}>
                    {item.label}
                  </button>;
          })}
            </nav>
          </div>
        </>}

      {/* Spacer to prevent content jump */}
      <div className={`transition-all duration-300 ${isScrolled ? 'h-16' : 'h-20'}`} data-id="element-497" data-mp="1zelbxwyelzw" />
    </>;
};